// Carries "which upsell prompt sent them to checkout" across the Stripe
// redirect (#350).
//
// Without this, `subscribe_checkout_started` knows the touchpoint and
// `subscription_activated` does not — so GA4 can report how many prompts led to
// a checkout but not how many led to a *subscription*, which is the number tier
// calibration actually needs. sessionStorage (not local) on purpose: the
// attribution belongs to this browsing session, and a stale value must not
// attach itself to a renewal weeks later.
//
// Read is destructive: an activation is attributed once. A refresh of /account
// then reports no source rather than double-counting the conversion.
//
// The two money flows get SEPARATE keys. A single shared key made a top-up
// click indistinguishable from a subscribe click, and the reader on each side
// treats any stashed value as evidence of its own flow: a Solo subscriber who
// clicked "Buy credits" and then wandered to /account would have fired
// `subscription_activated` for a subscription bought weeks earlier, and an
// abandoned subscribe checkout followed by a credit pack would have reported
// `topup_purchased` with the subscribe touchpoint.

export const UPSELL_FLOWS = {
  SUBSCRIBE: 'subscribe',
  TOPUP: 'topup',
};

const KEYS = {
  [UPSELL_FLOWS.SUBSCRIBE]: 'tweedia_upsell_source_subscribe',
  [UPSELL_FLOWS.TOPUP]: 'tweedia_upsell_source_topup',
};

// `flow` is required and unvalidated flows are dropped rather than defaulted:
// a silent fallback to one of the two keys is exactly the cross-flow bleed this
// split exists to prevent.
export function rememberUpsellSource(source, flow) {
  const key = KEYS[flow];
  if (!source || !key) return;
  try {
    window.sessionStorage.setItem(key, source);
  } catch (e) {
    // sessionStorage unavailable (private mode / blocked) — attribution is
    // best-effort; never let it break a checkout.
  }
}

export function consumeUpsellSource(flow) {
  const key = KEYS[flow];
  if (!key) return null;
  try {
    const value = window.sessionStorage.getItem(key);
    if (value) window.sessionStorage.removeItem(key);
    return value || null;
  } catch (e) {
    return null;
  }
}

export function peekUpsellSource(flow) {
  const key = KEYS[flow];
  if (!key) return null;
  try {
    return window.sessionStorage.getItem(key) || null;
  } catch (e) {
    return null;
  }
}

// The tier the account was on when it left for the Stripe billing portal.
//
// A plan change made in the portal returns through `return_url` as a FULL page
// load, so the funnel hook's `seenRef` is empty and the first snapshot usually
// already carries the new tier — there is no transition left to observe, and
// `tier_upgraded` / `tier_downgraded` would never fire for the portal path.
// Stashing the before-tier gives that first snapshot something to compare
// against. Consumed on the first snapshot so a later real transition is not
// counted twice.
const PORTAL_TIER_KEY = 'tweedia_billing_tier_before_portal';

export function rememberPortalTier(tier) {
  if (!tier) return;
  try {
    window.sessionStorage.setItem(PORTAL_TIER_KEY, tier);
  } catch (e) {
    // Best-effort, as above.
  }
}

export function consumePortalTier() {
  try {
    const value = window.sessionStorage.getItem(PORTAL_TIER_KEY);
    if (value) window.sessionStorage.removeItem(PORTAL_TIER_KEY);
    return value || null;
  } catch (e) {
    return null;
  }
}
